import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import Modal from '../../components/common/Modal';
import { useSocketEvent } from '../../hooks/useSocket';
import { isSocketConnected } from '../../services/socket';

// ═══════════════════════════════════════════════════════════════
// STATUS BADGES
// ═══════════════════════════════════════════════════════════════
const DisbursementStatusBadge = ({ status }) => {
  const map = {
    initiated:    { label: '⏳ Initiated',    color: '#6366f1', bg: '#eef2ff', border: '#a5b4fc' },
    processing:   { label: '🔄 Processing',   color: '#d97706', bg: '#fffbeb', border: '#fbbf24' },
    success:      { label: '✅ Success',      color: '#059669', bg: '#ecfdf5', border: '#34d399' },
    failed:       { label: '❌ Failed',       color: '#dc2626', bg: '#fef2f2', border: '#f87171' },
    cancelled:    { label: '🚫 Cancelled',    color: '#6b7280', bg: '#f9fafb', border: '#9ca3af' },
  };
  const s = map[status] || map.initiated;
  return (
    <span style={{
      fontSize: 11, padding: '3px 10px', borderRadius: 20,
      fontWeight: 600, color: s.color, background: s.bg,
      border: `1px solid ${s.border}`, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
};

const PaymentSourceBadge = ({ source }) => {
  const map = {
    'c2b-auto':       { label: '📱 M-Pesa Auto',  color: '#059669', bg: '#ecfdf5', border: '#34d399' },
    'counter':        { label: '🏢 Counter',       color: '#2563eb', bg: '#eff6ff', border: '#60a5fa' },
    'manual':         { label: '📋 Manual',        color: '#d97706', bg: '#fffbeb', border: '#fbbf24' },
    'bank-transfer':  { label: '🏦 Bank',          color: '#7c3aed', bg: '#f5f3ff', border: '#a78bfa' },
    'mpesa-b2c':      { label: '💸 M-Pesa B2C',    color: '#0891b2', bg: '#ecfeff', border: '#22d3ee' },
  };
  const s = map[source] || map.manual;
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 12,
      fontWeight: 600, color: s.color, background: s.bg,
      border: `1px solid ${s.border}`, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════
// TABS CONFIG
// ═══════════════════════════════════════════════════════════════
const TABS = [
  { key: 'disbursements', label: '💸 Disbursements', icon: '💸' },
  { key: 'repayments',    label: '📥 Repayments',    icon: '📥' },
  { key: 'overpayments',  label: '🔄 Overpayments',  icon: '🔄' },
];

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function MpesaTransactions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') || 'disbursements';
  const setActiveTab = (key) => setSearchParams({ tab: key });

  // ─── Shared State ───
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [liveFlash, setLiveFlash] = useState(false);

  // ─── Disbursements State ───
  const [disbursements, setDisbursements] = useState([]);
  const [disbPagination, setDisbPagination] = useState(null);
  const [disbPage, setDisbPage] = useState(1);
  const [disbFilters, setDisbFilters] = useState({ status: '', search: '' });

  // ─── Repayments State ───
  const [repayments, setRepayments] = useState([]);
  const [repPagination, setRepPagination] = useState(null);
  const [repPage, setRepPage] = useState(1);
  const [repFilters, setRepFilters] = useState({ search: '' });

  // ─── Overpayments State ───
  const [overpayments, setOverpayments] = useState([]);
  const [overPagination, setOverPagination] = useState(null);
  const [overPage, setOverPage] = useState(1);

  // ─── Refund Modal State ───
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedRepayment, setSelectedRepayment] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '', method: 'mpesa-b2c' });
  const [refundLoading, setRefundLoading] = useState(false);

  // ─── Disbursement Detail Modal ───
  const [showDisbDetail, setShowDisbDetail] = useState(false);
  const [selectedDisbursement, setSelectedDisbursement] = useState(null);

  // ═══════════════════════════════════════════════════════════
  // FETCH: Disbursements
  // ═══════════════════════════════════════════════════════════
  const fetchDisbursements = useCallback(async (silent = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: disbPage, limit: 20 });
      if (disbFilters.status) params.append('status', disbFilters.status);
      if (disbFilters.search) params.append('search', disbFilters.search);
      const res = await api.get(`/disbursements?${params}`);
      setDisbursements(res.data.data?.disbursements || res.data.data || []);
      setDisbPagination(res.data.data?.pagination || null);
      if (!silent) setLastUpdate(Date.now());
    } catch (err) {
      console.error('Failed to fetch disbursements:', err);
    } finally {
      setLoading(false);
    }
  }, [disbPage, disbFilters]);

  // ═══════════════════════════════════════════════════════════
  // FETCH: Repayments (M-Pesa auto only)
  // ═══════════════════════════════════════════════════════════
  const fetchRepayments = useCallback(async (silent = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: repPage, limit: 20, paymentSource: 'c2b-auto' });
      if (repFilters.search) params.append('search', repFilters.search);
      const res = await api.get(`/repayments?${params}`);
      setRepayments(res.data.data?.repayments || []);
      setRepPagination(res.data.data?.pagination || null);
      if (!silent) setLastUpdate(Date.now());
    } catch (err) {
      console.error('Failed to fetch M-Pesa repayments:', err);
    } finally {
      setLoading(false);
    }
  }, [repPage, repFilters]);

  // ═══════════════════════════════════════════════════════════
  // FETCH: Overpayments
  // ═══════════════════════════════════════════════════════════
  const fetchOverpayments = useCallback(async (silent = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: overPage, limit: 20, overpayment: 'true' });
      const res = await api.get(`/repayments?${params}`);
      setOverpayments(res.data.data?.repayments || []);
      setOverPagination(res.data.data?.pagination || null);
      if (!silent) setLastUpdate(Date.now());
    } catch (err) {
      console.error('Failed to fetch overpayments:', err);
    } finally {
      setLoading(false);
    }
  }, [overPage]);

  // ═══════════════════════════════════════════════════════════
  // EFFECTS: Fetch on tab change & page/filter changes
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (activeTab === 'disbursements') fetchDisbursements();
    else if (activeTab === 'repayments') fetchRepayments();
    else if (activeTab === 'overpayments') fetchOverpayments();
  }, [activeTab, fetchDisbursements, fetchRepayments, fetchOverpayments]);

  // ═══════════════════════════════════════════════════════════
  // SOCKET: Real-time auto-refresh
  // ═══════════════════════════════════════════════════════════
  const handleSocketUpdate = useCallback(() => {
    setLiveFlash(true);
    setTimeout(() => setLiveFlash(false), 3000);
    if (activeTab === 'disbursements') fetchDisbursements(true);
    else if (activeTab === 'repayments') fetchRepayments(true);
    else if (activeTab === 'overpayments') fetchOverpayments(true);
  }, [activeTab, fetchDisbursements, fetchRepayments, fetchOverpayments]);

  useSocketEvent('repayment:recorded', handleSocketUpdate);
  useSocketEvent('c2b:payment', handleSocketUpdate);
  useSocketEvent('disbursement:completed', handleSocketUpdate);

  // ═══════════════════════════════════════════════════════════
  // REFUND HANDLER
  // ═══════════════════════════════════════════════════════════
  const handleOpenRefund = (repayment) => {
    setSelectedRepayment(repayment);
    setRefundForm({
      amount: repayment.overpayment?.toString() || '',
      reason: '',
      method: 'mpesa-b2c',
    });
    setShowRefundModal(true);
  };

  const handleRefund = async () => {
    if (!refundForm.amount || parseFloat(refundForm.amount) <= 0) {
      alert('Please enter a valid refund amount');
      return;
    }
    if (!refundForm.reason.trim()) {
      alert('Please provide a reason for the refund');
      return;
    }
    setRefundLoading(true);
    try {
      await api.post(`/repayments/${selectedRepayment._id}/refund`, {
        refundAmount: parseFloat(refundForm.amount),
        refundMethod: refundForm.method,
        refundReason: refundForm.reason,
      });
      setShowRefundModal(false);
      // Refresh both repayments and overpayments
      fetchRepayments();
      fetchOverpayments();
    } catch (err) {
      alert(err.response?.data?.message || 'Refund failed. Please try again.');
    } finally {
      setRefundLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // PAGINATION HELPER
  // ═══════════════════════════════════════════════════════════
  const renderPagination = (pagination, page, setPage) => {
    if (!pagination) return null;
    return (
      <div className="flex justify-between items-center text-sm text-muted mt-2 p-2"
        style={{ borderTop: '1px solid #eee' }}>
        <span>Page {page} of {pagination.pages} ({pagination.total} total)</span>
        <div className="flex gap-1">
          <button className="btn btn-sm btn-outline" disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}>← Prev</button>
          <button className="btn btn-sm btn-outline" disabled={page >= pagination.pages}
            onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER: DISBURSEMENTS TAB
  // ═══════════════════════════════════════════════════════════
  const renderDisbursements = () => (
    <div>
      {/* Filters */}
      <div className="flex gap-2 mb-3" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>🔍</span>
          <input
            className="form-control"
            placeholder="Search by name, phone, receipt..."
            style={{ paddingLeft: 32 }}
            value={disbFilters.search}
            onChange={e => { setDisbFilters({ ...disbFilters, search: e.target.value }); setDisbPage(1); }}
          />
        </div>
        <select className="form-control" style={{ maxWidth: 180 }}
          value={disbFilters.status}
          onChange={e => { setDisbFilters({ ...disbFilters, status: e.target.value }); setDisbPage(1); }}>
          <option value="">All Statuses</option>
          <option value="initiated">⏳ Initiated</option>
          <option value="processing">🔄 Processing</option>
          <option value="success">✅ Success</option>
          <option value="failed">❌ Failed</option>
          <option value="cancelled">🚫 Cancelled</option>
        </select>
        {(disbFilters.status || disbFilters.search) && (
          <button className="btn btn-sm btn-outline"
            onClick={() => { setDisbFilters({ status: '', search: '' }); setDisbPage(1); }}>
            Clear
          </button>
        )}
        <button className="btn btn-sm btn-primary" onClick={() => navigate('/mpesa/link-code')}
          style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          🔗 Link Unmatched Code
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          <table style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>M-Pesa Receipt</th>
                <th>Client</th>
                <th>Phone</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Initiated By</th>
                <th>Initiated At</th>
                <th>Completed At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {disbursements.length === 0 ? (
                <tr><td colSpan="9" className="text-center p-4 text-muted">
                  {loading ? 'Loading...' : 'No disbursements found.'}
                </td></tr>
              ) : disbursements.map(row => (
                <tr key={row._id}
                  style={{ background: row.status === 'failed' ? '#fef2f2' : undefined }}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12 }}>
                    {row.mpesaReceipt || (
                      <span style={{ color: '#94a3b8' }}>—</span>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{row.clientName}</div>
                    <div className="text-xs text-muted">{row.clientNumber}</div>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.disbursementPhone}</td>
                  <td style={{ fontWeight: 700, color: '#1e40af' }}>
                    {formatCurrency(row.disbursementAmount)}
                  </td>
                  <td><DisbursementStatusBadge status={row.status} /></td>
                  <td style={{ fontSize: 12 }}>
                    <div>{row.initiatorName}</div>
                    <div className="text-xs text-muted">{row.initiatorRole}</div>
                  </td>
                  <td style={{ fontSize: 12 }}>{formatDate(row.initiatedAt)}</td>
                  <td style={{ fontSize: 12 }}>
                    {row.completedAt ? formatDate(row.completedAt) : (
                      <span style={{ color: '#94a3b8' }}>—</span>
                    )}
                  </td>
                  <td>
                    <button className="btn btn-sm btn-outline"
                      onClick={() => { setSelectedDisbursement(row); setShowDisbDetail(true); }}>
                      📋
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination(disbPagination, disbPage, setDisbPage)}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER: REPAYMENTS TAB
  // ═══════════════════════════════════════════════════════════
  const renderRepayments = () => (
    <div>
      {/* Filters */}
      <div className="flex gap-2 mb-3" style={{ alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>🔍</span>
          <input
            className="form-control"
            placeholder="Search by receipt, loan#, client..."
            style={{ paddingLeft: 32 }}
            value={repFilters.search}
            onChange={e => { setRepFilters({ ...repFilters, search: e.target.value }); setRepPage(1); }}
          />
        </div>
        {repFilters.search && (
          <button className="btn btn-sm btn-outline"
            onClick={() => { setRepFilters({ search: '' }); setRepPage(1); }}>
            Clear
          </button>
        )}
        <button className="btn btn-sm btn-primary" onClick={() => navigate('/mpesa/link-code')}
          style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          🔗 Link Unmatched Code
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          <table style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>M-Pesa Ref</th>
                <th>Loan #</th>
                <th>Client</th>
                <th>Amount</th>
                <th>Principal</th>
                <th>Interest</th>
                <th>Penalty</th>
                <th>Rate</th>
                <th>Day #</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {repayments.length === 0 ? (
                <tr><td colSpan="10" className="text-center p-4 text-muted">
                  {loading ? 'Loading...' : 'No M-Pesa repayments found.'}
                </td></tr>
              ) : repayments.map(row => (
                <tr key={row._id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 11 }}>
                    {row.paymentReference || '—'}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {row.loan?.loanNumber || '—'}
                  </td>
                  <td>
                    <div>{row.client?.firstName} {row.client?.lastName}</div>
                    <div className="text-xs text-muted">{row.client?.phone}</div>
                  </td>
                  <td style={{ fontWeight: 700 }}>{formatCurrency(row.amount)}</td>
                  <td style={{ color: '#059669' }}>{formatCurrency(row.principalAmount || 0)}</td>
                  <td style={{ color: '#d97706' }}>{formatCurrency(row.interestAmount || 0)}</td>
                  <td style={{ color: (row.penaltyAmount || 0) > 0 ? '#dc2626' : '#94a3b8' }}>
                    {formatCurrency(row.penaltyAmount || 0)}
                  </td>
                  <td>
                    <span style={{
                      fontSize: 11, padding: '2px 6px', borderRadius: 8,
                      fontWeight: 700,
                      color: (row.interestRateAtPayment || 0) >= 30 ? '#dc2626' :
                             (row.interestRateAtPayment || 0) > 14 ? '#d97706' : '#059669',
                      background: (row.interestRateAtPayment || 0) >= 30 ? '#fef2f2' :
                                  (row.interestRateAtPayment || 0) > 14 ? '#fffbeb' : '#ecfdf5',
                    }}>
                      {row.interestRateAtPayment || 0}%
                    </span>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {row.dayNumberAtPayment || '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>{formatDate(row.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination(repPagination, repPage, setRepPage)}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER: OVERPAYMENTS TAB
  // ═══════════════════════════════════════════════════════════
  const renderOverpayments = () => (
    <div>
      <div className="card mb-3" style={{
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        border: '1px solid #f59e0b', padding: '12px 16px',
      }}>
        <div className="flex items-center gap-2">
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700, color: '#92400e', fontSize: 14 }}>
              Overpayments — Client paid more than the outstanding balance
            </div>
            <div style={{ color: '#a16207', fontSize: 12 }}>
              Use the refund button to send the excess back to the client via M-Pesa B2C.
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>M-Pesa Ref</th>
                <th>Loan #</th>
                <th>Client</th>
                <th>Phone</th>
                <th>Amount Paid</th>
                <th>Overpayment</th>
                <th>Loan Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {overpayments.length === 0 ? (
                <tr><td colSpan="9" className="text-center p-4 text-muted">
                  {loading ? 'Loading...' : 'No overpayments found.'}
                </td></tr>
              ) : overpayments.map(row => {
                const overpaymentAmount = row.overpayment || 0;
                return (
                  <tr key={row._id} style={{ background: '#fffbeb' }}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 11 }}>
                      {row.paymentReference || '—'}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {row.loan?.loanNumber || '—'}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{row.client?.firstName} {row.client?.lastName}</div>
                      <div className="text-xs text-muted">{row.client?.nationalId}</div>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.client?.phone}</td>
                    <td style={{ fontWeight: 700 }}>{formatCurrency(row.amount)}</td>
                    <td style={{
                      fontWeight: 800, color: '#dc2626',
                      fontSize: 14, fontFamily: 'monospace',
                    }}>
                      +{formatCurrency(overpaymentAmount)}
                    </td>
                    <td>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 12,
                        fontWeight: 600,
                        color: row.loan?.status === 'closed' ? '#059669' : '#d97706',
                        background: row.loan?.status === 'closed' ? '#ecfdf5' : '#fffbeb',
                        border: `1px solid ${row.loan?.status === 'closed' ? '#34d399' : '#fbbf24'}`,
                      }}>
                        {row.loan?.status === 'closed' ? '✅ Closed' : row.loan?.status || '—'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>{formatDate(row.createdAt)}</td>
                    <td>
                      <button
                        className="btn btn-sm"
                        style={{
                          background: '#dc2626', color: '#fff',
                          fontSize: 11, padding: '4px 12px',
                        }}
                        onClick={() => handleOpenRefund(row)}
                        disabled={overpaymentAmount <= 0}
                      >
                        💸 Refund
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderPagination(overPagination, overPage, setOverPage)}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-3" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ margin: 0 }}>M-Pesa Transactions</h3>
          <p style={{ margin: '2px 0 0 0', fontSize: 13, color: '#64748b' }}>
            Disbursements, repayments & overpayments via M-Pesa
          </p>
        </div>

        {/* Live indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 20,
            background: liveFlash ? '#ecfdf5' : '#f1f5f9',
            border: `1px solid ${liveFlash ? '#10b981' : '#e2e8f0'}`,
            transition: 'all 0.3s ease',
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: liveFlash ? '#10b981' : (isSocketConnected() ? '#3b82f6' : '#94a3b8'),
              animation: liveFlash ? 'mpesaPulse 0.6s ease-in-out 3' : (isSocketConnected() ? 'mpesaBlink 2s infinite' : 'none'),
            }} />
            <span style={{
              color: liveFlash ? '#059669' : '#64748b',
              fontWeight: 600,
            }}>
              {liveFlash ? '🔴 LIVE' : (isSocketConnected() ? 'Auto-Updating' : 'Polling')}
            </span>
          </div>
          <span style={{ color: '#94a3b8', fontSize: 11 }}>
            Updated: {new Date(lastUpdate).toLocaleTimeString()}
          </span>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 0,
        borderBottom: '2px solid #e2e8f0', background: '#f8fafc',
        borderRadius: '8px 8px 0 0', overflow: 'hidden',
      }}>
        {TABS.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: isActive ? '#fff' : 'transparent',
                borderBottom: isActive ? '3px solid #2563eb' : '3px solid transparent',
                color: isActive ? '#1e40af' : '#64748b',
                fontWeight: isActive ? 700 : 500,
                fontSize: 14,
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: '-2px',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div style={{
        background: '#fff', borderRadius: '0 0 8px 8px',
        border: '1px solid #e2e8f0', borderTop: 'none',
        padding: 16,
      }}>
        {activeTab === 'disbursements' && renderDisbursements()}
        {activeTab === 'repayments' && renderRepayments()}
        {activeTab === 'overpayments' && renderOverpayments()}
      </div>

      {/* ═══════════════════════════════════════════════════════
          REFUND MODAL
          ═══════════════════════════════════════════════════════ */}
      <Modal isOpen={showRefundModal} onClose={() => setShowRefundModal(false)}
        title="💸 Refund Overpayment" size="480px">
        {selectedRepayment && (
          <div>
            {/* Client info */}
            <div style={{
              background: '#fef2f2', border: '1px solid #fca5a5',
              borderRadius: 8, padding: 12, marginBottom: 16,
            }}>
              <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: 6 }}>
                Refund Details
              </div>
              <div style={{ fontSize: 13, color: '#7f1d1d' }}>
                <div><strong>Client:</strong> {selectedRepayment.client?.firstName} {selectedRepayment.client?.lastName}</div>
                <div><strong>Phone:</strong> {selectedRepayment.client?.phone}</div>
                <div><strong>Payment:</strong> {formatCurrency(selectedRepayment.amount)} (Ref: {selectedRepayment.paymentReference})</div>
                <div><strong>Overpayment Amount:</strong>
                  <span style={{ color: '#dc2626', fontWeight: 800, fontSize: 16 }}>
                    {' '}{formatCurrency(selectedRepayment.overpayment || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Form */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Refund Amount (KES) *
              </label>
              <input
                type="number"
                className="form-control"
                value={refundForm.amount}
                onChange={e => setRefundForm({ ...refundForm, amount: e.target.value })}
                max={selectedRepayment.overpayment || 0}
                min="1"
                step="0.01"
                placeholder="Enter amount to refund"
              />
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                Max: {formatCurrency(selectedRepayment.overpayment || 0)}
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Refund Method *
              </label>
              <select className="form-control" value={refundForm.method}
                onChange={e => setRefundForm({ ...refundForm, method: e.target.value })}>
                <option value="mpesa-b2c">M-Pesa B2C (Send to phone)</option>
                <option value="manual">Manual (Cash/Bank — record only)</option>
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Reason for Refund *
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={refundForm.reason}
                onChange={e => setRefundForm({ ...refundForm, reason: e.target.value })}
                placeholder="e.g., Client overpaid KES 500 via M-Pesa. Refunding excess amount."
              />
            </div>

            {/* Warning */}
            <div style={{
              background: '#fffbeb', border: '1px solid #fbbf24',
              borderRadius: 8, padding: 10, marginBottom: 16,
              fontSize: 12, color: '#92400e',
            }}>
              ⚠️ <strong>Warning:</strong> M-Pesa B2C refunds are irreversible. Please verify the
              amount and phone number before confirming. This will be logged in the audit trail.
            </div>

            {/* Actions */}
            <div className="flex gap-2" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowRefundModal(false)}>
                Cancel
              </button>
              <button
                className="btn"
                style={{ background: '#dc2626', color: '#fff' }}
                onClick={handleRefund}
                disabled={refundLoading || !refundForm.amount || !refundForm.reason.trim()}
              >
                {refundLoading ? '⏳ Processing Refund...' : '💸 Confirm Refund'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ═══════════════════════════════════════════════════════
          DISBURSEMENT DETAIL MODAL
          ═══════════════════════════════════════════════════════ */}
      <Modal isOpen={showDisbDetail} onClose={() => setShowDisbDetail(false)}
        title="📋 Disbursement Detail" size="560px">
        {selectedDisbursement && (
          <div>
            <div style={{
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 8, padding: 14, marginBottom: 16,
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
                <div>
                  <div style={{ color: '#64748b', fontSize: 11, marginBottom: 2 }}>STATUS</div>
                  <DisbursementStatusBadge status={selectedDisbursement.status} />
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: 11, marginBottom: 2 }}>AMOUNT</div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#1e40af' }}>
                    {formatCurrency(selectedDisbursement.disbursementAmount)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: 11, marginBottom: 2 }}>CLIENT</div>
                  <div style={{ fontWeight: 600 }}>{selectedDisbursement.clientName}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: 11, marginBottom: 2 }}>DISBURSEMENT PHONE</div>
                  <div style={{ fontFamily: 'monospace' }}>{selectedDisbursement.disbursementPhone}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: 11, marginBottom: 2 }}>M-PESA RECEIPT</div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {selectedDisbursement.mpesaReceipt || '—'}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: 11, marginBottom: 2 }}>INITIATED BY</div>
                  <div>{selectedDisbursement.initiatorName}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{selectedDisbursement.initiatorRole}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: 11, marginBottom: 2 }}>INITIATED AT</div>
                  <div>{formatDate(selectedDisbursement.initiatedAt)}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: 11, marginBottom: 2 }}>COMPLETED AT</div>
                  <div>{selectedDisbursement.completedAt ? formatDate(selectedDisbursement.completedAt) : '—'}</div>
                </div>
              </div>
            </div>

            {/* M-Pesa Technical Details */}
            <div style={{
              background: '#f1f5f9', border: '1px solid #e2e8f0',
              borderRadius: 8, padding: 14, marginBottom: 16,
            }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                M-Pesa Technical Details
              </div>
              <div style={{ fontSize: 12, fontFamily: 'monospace', lineHeight: 2 }}>
                <div><span style={{ color: '#64748b' }}>Conversation ID:</span> {selectedDisbursement.conversationId || '—'}</div>
                <div><span style={{ color: '#64748b' }}>Originator Conv ID:</span> {selectedDisbursement.originatorConversationId || '—'}</div>
                <div><span style={{ color: '#64748b' }}>Transaction ID:</span> {selectedDisbursement.transactionId || '—'}</div>
                <div><span style={{ color: '#64748b' }}>Result Code:</span> {selectedDisbursement.resultCode || '—'}</div>
                <div><span style={{ color: '#64748b' }}>Result Desc:</span> {selectedDisbursement.resultDesc || '—'}</div>
                <div><span style={{ color: '#64748b' }}>Transaction Date:</span> {selectedDisbursement.transactionDate || '—'}</div>
              </div>
            </div>

            {/* Loan Details */}
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: 8, padding: 14, marginBottom: 16,
            }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#166534', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Loan Details
              </div>
              <div style={{ fontSize: 13 }}>
                <div><strong>Product:</strong> {selectedDisbursement.loanProduct || '—'}</div>
                <div><strong>Approved Amount:</strong> {formatCurrency(selectedDisbursement.approvedAmount)}</div>
                <div><strong>Duration:</strong> {selectedDisbursement.duration} {selectedDisbursement.durationUnit || 'days'}</div>
                <div><strong>Interest Rate:</strong> {selectedDisbursement.interestRate}%</div>
              </div>
            </div>

            {/* Failed disbursement actions */}
            {selectedDisbursement.status === 'failed' && selectedDisbursement.canRetry && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fca5a5',
                borderRadius: 8, padding: 12, marginBottom: 16,
              }}>
                <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: 6 }}>
                  ⚠️ Disbursement Failed
                </div>
                <div style={{ fontSize: 12, color: '#7f1d1d', marginBottom: 8 }}>
                  {selectedDisbursement.resultDesc || 'Unknown error'}
                </div>
                <div style={{ fontSize: 11, color: '#991b1b', marginBottom: 8 }}>
                  Retries: {selectedDisbursement.retryCount || 0}
                </div>
                <div className="flex gap-2">
                  <button className="btn btn-sm" style={{ background: '#dc2626', color: '#fff' }}
                    onClick={async () => {
                      try {
                        await api.post(`/disbursements/${selectedDisbursement._id}/retry`);
                        setShowDisbDetail(false);
                        fetchDisbursements();
                      } catch (err) {
                        alert(err.response?.data?.message || 'Retry failed');
                      }
                    }}>
                    🔄 Retry B2C
                  </button>
                </div>
              </div>
            )}

            {/* Notes */}
            {selectedDisbursement.notes && (
              <div style={{ fontSize: 13, color: '#475569', padding: '8px 0' }}>
                <strong>Notes:</strong> {selectedDisbursement.notes}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Animations */}
      <style>{`
        @keyframes mpesaPulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(2); opacity: 0.3; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes mpesaBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}